import { trackEvent } from '../../analytics';
import type { MathTopicId, ProgressState, StatusMessage } from './types';
import { MATH_TOPICS } from './types';

type GenerateTestPanelProps = {
  count: number;
  onCountChange: (value: number) => void;
  difficulty: number;
  onDifficultyChange: (value: number) => void;
  selectedTopics: Set<MathTopicId>;
  onToggleTopic: (topicId: MathTopicId) => void;
  onSetAllTopics: (selected: boolean) => void;
  onGenerate: () => void | Promise<void>;
  loading: boolean;
  generation: ProgressState | null;
  progressPercent: number;
  status: StatusMessage | null;
  hasTest: boolean;
};

export function GenerateTestPanel({
  count,
  onCountChange,
  difficulty,
  onDifficultyChange,
  selectedTopics,
  onToggleTopic,
  onSetAllTopics,
  onGenerate,
  loading,
  generation,
  progressPercent,
  status,
  hasTest,
}: GenerateTestPanelProps) {
  const handleGenerateClick = () => {
    trackEvent('test_generate_clicked', {
      questionCount: count,
      difficulty,
      selectedTopicCount: selectedTopics.size,
      allTopicsSelected: selectedTopics.size === MATH_TOPICS.length,
      hasExistingTest: hasTest,
    });
    void onGenerate();
  };

  return (
    <div className="card card-primary card-outline h-100">
      <div className="card-header border-0">
        <h3 className="card-title mb-0">
          <i className="fas fa-magic me-2" aria-hidden="true" /> Generate Test
        </h3>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <label className="form-label fw-semibold" htmlFor="questionCount">
            Number of questions
          </label>
          <input
            id="questionCount"
            type="number"
            min={1}
            max={40}
            className="form-control"
            value={count}
            onChange={(e) => onCountChange(parseInt(e.target.value || '5', 10))}
          />
          <div className="form-text">Between 1 and 40 questions.</div>
        </div>

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <label className="form-label fw-semibold mb-0" htmlFor="difficultyRange">
              Difficulty
            </label>
            <span className="badge bg-primary">{difficulty}/10</span>
          </div>
          <input
            id="difficultyRange"
            type="range"
            className="form-range"
            min={1}
            max={10}
            value={difficulty}
            onChange={(e) => onDifficultyChange(parseInt(e.target.value, 10))}
          />
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="h6 mb-0 text-muted">Select topics</h4>
            <div className="btn-group btn-group-sm" role="group" aria-label="Topic selection actions">
              <button type="button" className="btn btn-outline-primary" onClick={() => onSetAllTopics(true)}>
                Select all
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => onSetAllTopics(false)}>
                Clear
              </button>
            </div>
          </div>
          <div className="row g-2 topic-grid">
            {MATH_TOPICS.map((topic) => (
              <div key={topic.id} className="col-12 col-sm-6">
                <div className="form-check form-switch bg-light rounded px-3 py-2 border">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`topic-${topic.id}`}
                    checked={selectedTopics.has(topic.id)}
                    onChange={() => onToggleTopic(topic.id)}
                  />
                  <label className="form-check-label fw-semibold" htmlFor={`topic-${topic.id}`} title={topic.description}>
                    {topic.label}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {generation && (
          <div className="mb-3">
            <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPercent}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ width: `${progressPercent}%` }}
              >
                {progressPercent}%
              </div>
            </div>
            <p className="text-muted small mb-0 mt-2">
              {generation.progress.total > 0
                ? `Overall progress: ${Math.min(generation.progress.current, generation.progress.total)} of ${generation.progress.total} questions generated.`
                : 'Working on questions…'}
            </p>
            <p className="text-muted small mb-0">
              Latest update: {generation.progress.message || 'Starting generation pipeline.'}
            </p>
          </div>
        )}

        {status && status.text && (
          <div
            className={`alert alert-${status.type === 'err' ? 'danger' : status.type === 'ok' ? 'success' : 'info'} alert-dismissible fade show`}
            role="status"
          >
            {status.text}
          </div>
        )}
      </div>
      <div className="card-footer bg-white">
        <button type="button" className="btn btn-primary w-100" onClick={handleGenerateClick} disabled={loading}>
          {loading ? 'Generating…' : hasTest ? 'Generate new test' : 'Generate test'}
        </button>
      </div>
    </div>
  );
}
